import { createLocation, useHistory } from 'quartz-routing/react-router';
import { useCallback } from 'react';
import { shouldReloadLocation } from './useShouldReloadLocation';
import { getWindow } from '../utils/browser';
/**
 * Returns a navigate function that is aware of Academy's multi-app deployment.
 * For same-app targets it uses `history.push`/`replace`; for cross-app targets
 * it triggers a full page reload via `window.location`.
 *
 * Router state is not supported. A target may resolve to a full page load,
 * which cannot carry it, so `to` is typed without `state` and any state that
 * reaches this hook untyped is stripped. Anything that has to survive a
 * navigation belongs in the target's `search`.
 */
export const useACNavigate = () => {
  const history = useHistory();
  const navigate = useCallback((to, options = {}) => {
    if (typeof to === 'number') {
      history.go(to);
      return;
    }
    const {
      replace = false,
      forceReload = false
    } = options;
    const resolvedTo = typeof to === 'function' ? to(history.location) : to;

    // `state: undefined` is not redundant: `to` is often built by spreading
    // the current location, which carries state the type cannot reject.
    const toLocation = Object.assign({}, createLocation(resolvedTo, undefined, undefined, history.location), {
      state: undefined
    });
    if (forceReload || shouldReloadLocation(toLocation)) {
      var _options$href;
      const href = (_options$href = options.href) !== null && _options$href !== void 0 ? _options$href : history.createHref(toLocation);
      if (replace) {
        getWindow().location.replace(href);
      } else {
        getWindow().location.assign(href);
      }
      return;
    }
    if (replace) {
      history.replace(toLocation);
    } else {
      history.push(toLocation);
    }
  }, [history]);
  return navigate;
};